import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminLogin, adminVerify } from '../utils/adminSession';
import { QuestionnaireAdminLoginView } from '../components/admin/questionnaire/QuestionnaireAdminLoginView';
import { QuestionnaireAdminTypeTabs } from '../components/admin/questionnaire/QuestionnaireAdminTypeTabs';
import { QuestionnaireAdminSectionsList } from '../components/admin/questionnaire/QuestionnaireAdminSectionsList';
import { QuestionnaireAdminSectionModal } from '../components/admin/questionnaire/QuestionnaireAdminSectionModal';
import { QuestionnaireAdminQuestionModal } from '../components/admin/questionnaire/QuestionnaireAdminQuestionModal';
import { DEFAULT_QUESTIONNAIRE_SECTIONS } from '../data/questionnaireDefaultSections';
import { fetchQuestionnaireSections } from '../utils/questionnaireAdminApi';

const QuestionnaireAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [loginError, setLoginError] = useState('');
  
  const [activeTab, setActiveTab] = useState('casas');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showAddSection, setShowAddSection] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showEditQuestion, setShowEditQuestion] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  
  const [sectionForm, setSectionForm] = useState({
    title_jp: '', title_br: '', title_us: '', title_es: '', order: 1
  });
  
  const [questionForm, setQuestionForm] = useState({
    type: 'text',
    required: false,
    text_jp: '', text_br: '', text_us: '', text_es: '',
    options: ''
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await adminVerify();
      setIsAuthenticated(Boolean(response.data.valid));
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsChecking(false);
    }
  }, [adminVerify]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await adminLogin(password);
      if (response.data.success) {
        setIsAuthenticated(true);
      }
    } catch (err) {
      setLoginError('Senha incorreta');
    }
  };

  const loadSections = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchQuestionnaireSections(activeTab);
      setSections(response || DEFAULT_QUESTIONNAIRE_SECTIONS[activeTab]);
    } catch {
      setSections(DEFAULT_QUESTIONNAIRE_SECTIONS[activeTab]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    loadSections();
  }, [isAuthenticated, loadSections]);

  const handleAddSection = async (e) => {
    e.preventDefault();
    const newSection = {
      id: Date.now(),
      order: sections.length + 1,
      ...sectionForm,
      questions: []
    };
    setSections([...sections, newSection]);
    setShowAddSection(false);
    setSectionForm({ title_jp: '', title_br: '', title_us: '', title_es: '', order: 1 });
  };

  const handleDeleteSection = (sectionId) => {
    if (window.confirm('Excluir esta seção e todas as perguntas?')) {
      setSections(sections.filter(s => s.id !== sectionId));
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    const newQuestion = {
      id: Date.now(),
      ...questionForm,
      options: questionForm.options.split(',').map(o => o.trim()).filter(o => o)
    };
    
    setSections(sections.map(s => {
      if (s.id === selectedSection.id) {
        return { ...s, questions: [...s.questions, newQuestion] };
      }
      return s;
    }));
    
    setShowAddQuestion(false);
    setQuestionForm({ type: 'text', required: false, text_jp: '', text_br: '', text_us: '', text_es: '', options: '' });
  };

  const handleEditQuestion = async (e) => {
    e.preventDefault();
    
    setSections(sections.map(s => {
      if (s.id === selectedSection.id) {
        return {
          ...s,
          questions: s.questions.map(q => {
            if (q.id === selectedQuestion.id) {
              return {
                ...q,
                ...questionForm,
                options: typeof questionForm.options === 'string' 
                  ? questionForm.options.split(',').map(o => o.trim()).filter(o => o)
                  : questionForm.options
              };
            }
            return q;
          })
        };
      }
      return s;
    }));
    
    setShowEditQuestion(false);
    setSelectedQuestion(null);
  };

  const handleDeleteQuestion = (sectionId, questionId) => {
    if (window.confirm('Excluir esta pergunta?')) {
      setSections(sections.map(s => {
        if (s.id === sectionId) {
          return { ...s, questions: s.questions.filter(q => q.id !== questionId) };
        }
        return s;
      }));
    }
  };

  const openEditQuestion = (section, question) => {
    setSelectedSection(section);
    setSelectedQuestion(question);
    setQuestionForm({
      type: question.type,
      required: question.required,
      text_jp: question.text_jp,
      text_br: question.text_br,
      text_us: question.text_us || '',
      text_es: question.text_es || '',
      options: Array.isArray(question.options) ? question.options.join(', ') : ''
    });
    setShowEditQuestion(true);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <QuestionnaireAdminLoginView
        password={password}
        setPassword={setPassword}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        loginError={loginError}
        handleLogin={handleLogin}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/admin" className="text-gray-600 hover:text-gray-900" data-testid="questionnaire-admin-header-back-link">
            ← Voltar
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-2xl">📝</span>
            <h1 className="text-xl font-bold text-gray-900">Editor de Questionários</h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <QuestionnaireAdminTypeTabs activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Sections Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900" data-testid="questionnaire-admin-section-count">
            Seções do Questionário ({sections.length})
          </h2>
          <button
            onClick={() => setShowAddSection(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center gap-2"
            data-testid="questionnaire-admin-add-section-button"
          >
            + Nova Seção
          </button>
        </div>

        <QuestionnaireAdminSectionsList
          sections={sections}
          setSelectedSection={setSelectedSection}
          setShowAddQuestion={setShowAddQuestion}
          handleDeleteSection={handleDeleteSection}
          openEditQuestion={openEditQuestion}
          handleDeleteQuestion={handleDeleteQuestion}
        />
      </div>

      <QuestionnaireAdminSectionModal
        isOpen={showAddSection}
        sectionForm={sectionForm}
        setSectionForm={setSectionForm}
        onClose={() => setShowAddSection(false)}
        onSubmit={handleAddSection}
      />

      <QuestionnaireAdminQuestionModal
        isOpen={showAddQuestion}
        mode="add"
        questionForm={questionForm}
        setQuestionForm={setQuestionForm}
        onClose={() => setShowAddQuestion(false)}
        onSubmit={handleAddQuestion}
      />

      <QuestionnaireAdminQuestionModal
        isOpen={showEditQuestion}
        mode="edit"
        questionForm={questionForm}
        setQuestionForm={setQuestionForm}
        onClose={() => setShowEditQuestion(false)}
        onSubmit={handleEditQuestion}
      />
    </div>
  );
};

export default QuestionnaireAdmin;
